import { computed, inject, signal } from '@angular/core';
import { debounceTime, firstValueFrom, forkJoin, Subject } from 'rxjs';
import { map as rxjsMap } from 'rxjs/operators';
import { AgentTaskStatus } from 'AgentApp/ts/interfaces/agent-task.interface';
import { AjaxService } from 'Angular/services/core/ajax.service';
import { UrlsService } from 'Angular/services/core/urls.service';
import { serializeObjectSortedKeys } from 'Angular/utils/urls';
import * as i0 from "@angular/core";
const DEFAULT_LOADING_TEXT = 'Considering...';
// Tools whose matches are documents; everything else renders its items as links.
const FILE_ITEM_TOOLS = new Set(['list_reports']);
// stored tool results have a different key than their streaming counterpart
const STORED_ITEMS_KEYS = {
    search_help_articles: 'help_articles',
    get_help_article: 'help_article',
    search_news: 'news_articles',
    search_securities: 'security_results',
    search_economic_indicators: 'indicator_results',
    list_dashboards: 'results',
    search_saved_assets: 'results',
};
const storedItems = (toolName, parsedResult) => {
    const value = parsedResult?.[STORED_ITEMS_KEYS[toolName] ?? 'items'];
    return value && !Array.isArray(value) && typeof value === 'object' ? [value] : value;
};
const toStepItems = (toolName, rawItems) => {
    if (!Array.isArray(rawItems)) {
        return undefined;
    }
    const kind = FILE_ITEM_TOOLS.has(toolName) ? 'file' : 'link';
    const items = rawItems
        .filter((item) => !!item && typeof item.url === 'string')
        .map((item) => ({ title: item.title || item.name || item.url, url: item.url, kind }));
    return items.length ? items : undefined;
};
const SIDEBAR_PAGE_SIZE = 10;
const SIDEBAR_PINNED_CAP = 10;
const SEARCH_DEBOUNCE_MS = 300;
const createEmptyChatState = () => {
    return {
        messages: [],
        isStreaming: false,
        isLoading: false,
        currentLoadingText: DEFAULT_LOADING_TEXT,
        agentSteps: [],
        toolCallsToConfirm: [],
        queuedMessages: [],
        _currentAssistantText: '',
        _currentThinkingText: '',
        _pendingDisplayActions: [],
    };
};
export class AgentSessionStateService {
    /**
     * Whether a persisted user-role message is a system notification (for example a portfolio the agent
     * created finishing its calculation). The proxy wraps those in a <system_notification> tag; they exist
     * for the model and are never shown to the user.
     */
    isSystemNotification(content) {
        const textBlocks = content.filter((block) => block.type === 'text');
        return (textBlocks.length > 0 &&
            textBlocks.every((block) => (block.text ?? '').trimStart().startsWith('<system_notification')));
    }
    getChatState(sessionId) {
        if (!sessionId) {
            return createEmptyChatState();
        }
        return this.chatStates().get(sessionId) ?? createEmptyChatState();
    }
    getLastMessageStreamId(sessionId) {
        return this.lastMessageStreamIds().get(sessionId) ?? '';
    }
    constructor() {
        this.toolCallCompleted$ = new Subject();
        this._ajaxService = inject(AjaxService);
        this._urlsService = inject(UrlsService);
        this._pinnedSessions = signal([], ...(ngDevMode ? [{ debugName: "_pinnedSessions" }] : []));
        this._recentChatSessions = signal([], ...(ngDevMode ? [{ debugName: "_recentChatSessions" }] : []));
        this._recentTaskSessions = signal([], ...(ngDevMode ? [{ debugName: "_recentTaskSessions" }] : []));
        this.searchQuery = signal('', ...(ngDevMode ? [{ debugName: "searchQuery" }] : []));
        this.isLoadingSessions = signal(false, ...(ngDevMode ? [{ debugName: "isLoadingSessions" }] : []));
        this.pinnedSessions = computed(() => this._pinnedSessions(), ...(ngDevMode ? [{ debugName: "pinnedSessions" }] : []));
        this.taskSessions = computed(() => this._recentTaskSessions(), ...(ngDevMode ? [{ debugName: "taskSessions" }] : []));
        this.chatSessionGroups = computed(() => {
            const sessions = this._recentChatSessions();
            return sessions.length ? [{ label: 'History', sessions }] : [];
        }, ...(ngDevMode ? [{ debugName: "chatSessionGroups" }] : []));
        this._searchInput$ = new Subject();
        this._lastFetchedSearchQuery = '';
        this.allAgentTasks = signal([], ...(ngDevMode ? [{ debugName: "allAgentTasks" }] : []));
        this.visibleTasks = computed(() => this.allAgentTasks().filter((task) => !task.is_hidden), ...(ngDevMode ? [{ debugName: "visibleTasks" }] : []));
        this.completedTasks = computed(() => this.visibleTasks().filter((task) => task.status === AgentTaskStatus.COMPLETE), ...(ngDevMode ? [{ debugName: "completedTasks" }] : []));
        this.inProgressTasks = computed(() => this.visibleTasks().filter((task) => task.status === AgentTaskStatus.IN_PROGRESS), ...(ngDevMode ? [{ debugName: "inProgressTasks" }] : []));
        this.cancelledTasks = computed(() => this.visibleTasks().filter((task) => task.status === AgentTaskStatus.CANCELLED), ...(ngDevMode ? [{ debugName: "cancelledTasks" }] : []));
        this.hasInProgressTasks = computed(() => this.inProgressTasks().length > 0, ...(ngDevMode ? [{ debugName: "hasInProgressTasks" }] : []));
        this.taskCompletionPercentage = computed(() => {
            const total = this.visibleTasks().length;
            const completed = this.completedTasks().length;
            if (total === 0) {
                return 0;
            }
            return Math.round((completed / total) * 100);
        }, ...(ngDevMode ? [{ debugName: "taskCompletionPercentage" }] : []));
        this.pendingNewSessionMessage = signal(null, ...(ngDevMode ? [{ debugName: "pendingNewSessionMessage" }] : []));
        this.chatStates = signal(new Map(), ...(ngDevMode ? [{ debugName: "chatStates" }] : []));
        this.lastMessageStreamIds = signal(new Map(), ...(ngDevMode ? [{ debugName: "lastMessageStreamIds" }] : []));
        this._searchInput$.pipe(debounceTime(SEARCH_DEBOUNCE_MS)).subscribe((query) => {
            this._refreshRecentSessions(query);
        });
    }
    loadSidebarSessions(options = {}) {
        const query = this.searchQuery();
        this._lastFetchedSearchQuery = query;
        if (!options.silent) {
            this.isLoadingSessions.set(true);
        }
        forkJoin({
            pinned: this._fetchSessions({ pinned: true }),
            chats: this._fetchSessions({
                pinned: false,
                sessionType: { chats: true, tasks: false },
                name: query,
            }),
            tasks: this._fetchSessions({
                pinned: false,
                sessionType: { chats: false, tasks: true },
                name: query,
            }),
        }).subscribe({
            next: ({ pinned, chats, tasks }) => {
                if (this._lastFetchedSearchQuery !== query) {
                    return;
                }
                this._pinnedSessions.set(pinned);
                this._recentChatSessions.set(chats);
                this._recentTaskSessions.set(tasks);
                if (!options.silent) {
                    this.isLoadingSessions.set(false);
                }
            },
            error: () => {
                if (!options.silent) {
                    this.isLoadingSessions.set(false);
                }
            },
        });
    }
    onSearchChange(query) {
        this.searchQuery.set(query);
        this._searchInput$.next(query);
    }
    deleteSession(session) {
        this.deleteSessionById(session.id);
    }
    deleteSessionById(sessionId) {
        const url = this._urlsService.getUrl(this._urlsService.AGENT.SESSION_DETAIL, {
            agentSessionId: sessionId.toString(),
        });
        this._ajaxService.deleteDataObserveBody(url).subscribe({
            next: () => {
                this._pinnedSessions.update((s) => s.filter((x) => x.id !== sessionId));
                this._recentChatSessions.update((s) => s.filter((x) => x.id !== sessionId));
                this._recentTaskSessions.update((s) => s.filter((x) => x.id !== sessionId));
                this._removeChatState(sessionId);
                this.loadSidebarSessions({ silent: true });
            },
            error: () => { },
        });
    }
    updateSession(session) {
        const url = this._urlsService.getUrl(this._urlsService.AGENT.SESSION_DETAIL, {
            agentSessionId: session.id.toString(),
        });
        this._ajaxService.putDataObserveBody(url, session).subscribe({
            next: () => {
                this._upsertSession(session);
            },
            error: () => { },
        });
    }
    shareSession(_session) {
        // TODO: share chat session via API
    }
    addSessionToRecent(session) {
        this._upsertSession(session);
    }
    bumpSessionToTop(sessionId) {
        const session = this._pinnedSessions().find((s) => s.id === sessionId) ??
            this._recentChatSessions().find((s) => s.id === sessionId) ??
            this._recentTaskSessions().find((s) => s.id === sessionId);
        if (session) {
            this._upsertSession(session);
        }
    }
    _fetchSessions(options) {
        const url = this._urlsService.getUrl(this._urlsService.AGENT.SESSION_SIDEBAR_COLLECTION);
        const params = {
            page: 1,
            sortColumn: 'user_modify_date',
            sortDirection: 'desc',
        };
        if (options.pinned !== undefined) {
            params.pinned = options.pinned ? 'true' : 'false';
        }
        if (options.sessionType) {
            params.sessionType = serializeObjectSortedKeys(options.sessionType);
        }
        if (options.name) {
            params.name = options.name;
        }
        return this._ajaxService
            .getDataObserveBody(url, params)
            .pipe(rxjsMap((response) => response.data.items.map(this._toChatSession)));
    }
    _refreshRecentSessions(query) {
        this._lastFetchedSearchQuery = query;
        forkJoin({
            chats: this._fetchSessions({
                pinned: false,
                sessionType: { chats: true, tasks: false },
                name: query,
            }),
            tasks: this._fetchSessions({
                pinned: false,
                sessionType: { chats: false, tasks: true },
                name: query,
            }),
        }).subscribe({
            next: ({ chats, tasks }) => {
                if (this._lastFetchedSearchQuery !== query) {
                    return;
                }
                this._recentChatSessions.set(chats);
                this._recentTaskSessions.set(tasks);
            },
            error: () => { },
        });
    }
    _toChatSession(item) {
        return {
            id: item.id,
            name: item.name,
            pinned: item.pinned,
            provider: item.provider,
            ...(item.agent_prompt_id !== null ? { agent_prompt_id: item.agent_prompt_id } : {}),
        };
    }
    _upsertSession(session) {
        const removeById = (arr) => arr.filter((s) => s.id !== session.id);
        this._pinnedSessions.update(removeById);
        this._recentChatSessions.update(removeById);
        this._recentTaskSessions.update(removeById);
        if (session.pinned) {
            this._pinnedSessions.update((arr) => [session, ...arr].slice(0, SIDEBAR_PINNED_CAP));
            return;
        }
        const bucket = session.agent_prompt_id ? this._recentTaskSessions : this._recentChatSessions;
        bucket.update((arr) => [session, ...arr].slice(0, SIDEBAR_PAGE_SIZE));
    }
    selectTask(_task) {
        // TODO: navigate to task
    }
    deleteTask(task) {
        this.allAgentTasks.update((tasks) => tasks.filter((t) => t.id !== task.id));
    }
    hideTask(task) {
        this.allAgentTasks.update((tasks) => tasks.map((t) => (t.id === task.id ? { ...t, is_hidden: true } : t)));
    }
    async loadMessages(sessionId, isActive = false) {
        const url = this._urlsService.getUrl(this._urlsService.AGENT.SESSION_MESSAGES, {
            agentSessionId: sessionId.toString(),
        });
        const { messages, last_message_stream_id, provider } = await firstValueFrom(this._ajaxService.getDataObserveBody(url));
        const toolUseNames = new Map();
        const toolDescriptions = new Map();
        const parsedToolResults = new Map();
        messages.forEach((msg) => {
            (msg.content ?? []).forEach((block) => {
                if (block.type === 'tool_use' && block.id) {
                    toolUseNames.set(block.id, block.name);
                    if (block.description) {
                        toolDescriptions.set(block.id, block.description);
                    }
                }
                else if (block.type === 'tool_result' && block.tool_use_id && block.content) {
                    try {
                        const parsed = typeof block.content === 'string' ? JSON.parse(block.content) : block.content;
                        if (parsed) {
                            parsedToolResults.set(block.tool_use_id, parsed);
                        }
                    }
                    catch {
                        // Not parseable, skip
                    }
                }
            });
        });
        let pendingActions = [];
        let pendingSteps = [];
        // Display actions attach to the LAST text message of the agent turn (matching
        // streaming, where they attach on done) — not to intermediate text the agent
        // emits between tool calls. Track that message's index until the turn ends.
        let lastTurnTextIndex = -1;
        const chatMessages = messages.reduce((acc, msg) => {
            const content = msg.content ?? [];
            if (msg.role === 'user') {
                const toolResults = content.filter((block) => block.type === 'tool_result');
                if (toolResults.length) {
                    toolResults.forEach((block) => {
                        const parsed = parsedToolResults.get(block.tool_use_id);
                        if (!parsed) {
                            return;
                        }
                        // Only chart results (state) and report downloads render below the
                        // message — other tool links surface on their See Details steps.
                        // PDF report tools persist their download link in the model-facing
                        // context as report_download_url/report_title (or zip_download_url for a
                        // zip archive) rather than url/title; mirror the tool's display_url choice.
                        if (parsed.report_download_url || parsed.zip_download_url) {
                            pendingActions.push({
                                name: toolUseNames.get(block.tool_use_id) || '',
                                display_result: {
                                    title: parsed.report_title || '',
                                    url: parsed.report_download_url || parsed.zip_download_url,
                                    type: 'download',
                                },
                            });
                        }
                        else if (parsed.state) {
                            pendingActions.push({
                                name: toolUseNames.get(block.tool_use_id) || '',
                                display_result: {
                                    title: parsed.title || '',
                                    url: parsed.url || '',
                                    state: parsed.state,
                                },
                            });
                        }
                    });
                    // don't render user response with tool_results
                    const hasOnlyToolResults = content.every((block) => block.type === 'tool_result');
                    if (hasOnlyToolResults) {
                        return acc;
                    }
                }
                // System notifications (a portfolio finished calculating) are persisted under the user
                // role for the model's benefit only; the user never wrote them, so they are not shown.
                if (this.isSystemNotification(content)) {
                    // It still ends the previous agent turn, as done does when streaming: that turn's
                    // display actions and steps stay with it instead of carrying over to the reply.
                    if (pendingActions.length && lastTurnTextIndex !== -1) {
                        acc[lastTurnTextIndex].displayActions = pendingActions;
                    }
                    if (pendingSteps.length) {
                        acc.push({ role: 'assistant', text: '', files: [], steps: pendingSteps });
                    }
                    pendingActions = [];
                    pendingSteps = [];
                    lastTurnTextIndex = -1;
                    return acc;
                }
                const text = content
                    .filter((block) => block.type === 'text')
                    .map((block) => block.text)
                    .join('\n');
                if (text) {
                    // a real user message ends the previous agent turn — flush its
                    // display actions to that turn's last text message
                    if (pendingActions.length && lastTurnTextIndex !== -1) {
                        acc[lastTurnTextIndex].displayActions = pendingActions;
                    }
                    pendingActions = [];
                    lastTurnTextIndex = -1;
                    acc.push({ role: 'user', text, files: msg.files ?? [] });
                }
            }
            else if (msg.role === 'assistant') {
                const stepsBeforeText = [];
                const stepsAfterText = [];
                const textParts = [];
                let hasSeenText = false;
                content.forEach((block) => {
                    if (block.type === 'thinking' && block.thinking) {
                        const step = {
                            type: 'thinking',
                            title: 'Thought',
                            description: block.thinking,
                        };
                        (hasSeenText ? stepsAfterText : stepsBeforeText).push(step);
                    }
                    else if (block.type === 'tool_use') {
                        // Report downloads render a dedicated download button below the
                        // message, so their download url is not repeated on the step title.
                        const result = parsedToolResults.get(block.id);
                        const step = {
                            type: 'tool_call',
                            title: toolDescriptions.get(block.id) || block.display_message || block.name,
                            description: '',
                            toolUseId: block.id,
                            url: result?.url || undefined,
                            items: toStepItems(block.name, storedItems(block.name, result)),
                        };
                        (hasSeenText ? stepsAfterText : stepsBeforeText).push(step);
                    }
                    else if (block.type === 'server_tool_use' && block.name === 'web_search') {
                        const query = block.input?.query;
                        const step = {
                            type: 'tool_call',
                            title: query ? `Searched the web for "${query}"` : 'Searched the web',
                            description: '',
                        };
                        (hasSeenText ? stepsAfterText : stepsBeforeText).push(step);
                    }
                    else if (block.type === 'text' && block.text) {
                        hasSeenText = true;
                        textParts.push(block.text);
                    }
                });
                const text = textParts.join('\n');
                const allStepsBeforeText = [...pendingSteps, ...stepsBeforeText];
                if (text) {
                    pendingSteps = stepsAfterText;
                    acc.push({
                        role: 'assistant',
                        text,
                        files: [],
                        steps: allStepsBeforeText.length ? allStepsBeforeText : undefined,
                    });
                    lastTurnTextIndex = acc.length - 1;
                }
                else if (allStepsBeforeText.length || stepsAfterText.length) {
                    // steps-only message - accumulate for the next text message
                    pendingSteps = [...allStepsBeforeText, ...stepsAfterText];
                }
            }
            return acc;
        }, []);
        // Detect unresolved tool_use blocks in the last assistant message
        // (no following user message with matching tool_results means confirmation is pending)
        let pendingToolConfirmations = [];
        if (messages.length) {
            const lastMsg = messages[messages.length - 1];
            if (lastMsg.role === 'assistant') {
                const toolUseBlocks = (lastMsg.content ?? []).filter((block) => block.type === 'tool_use');
                if (toolUseBlocks.length) {
                    pendingToolConfirmations = toolUseBlocks.map((block) => ({
                        tool_use_id: block.id,
                        input: block.input,
                        name: block.name,
                    }));
                }
            }
        }
        // End of conversation closes the final agent turn: flush its display actions to the
        // turn's last text message — unless an active stream will attach them on its done
        // event. A pending confirmation always flushes: the transport never opens a stream
        // while a confirmation is unanswered, so no done event would attach them.
        if (pendingActions.length && lastTurnTextIndex !== -1 && (pendingToolConfirmations.length || !isActive)) {
            chatMessages[lastTurnTextIndex].displayActions = pendingActions;
            pendingActions = [];
        }
        // flush any remaining steps-only messages that had no following text when not streaming
        if (pendingSteps.length && !pendingToolConfirmations.length && !isActive) {
            chatMessages.push({
                role: 'assistant',
                text: '',
                files: [],
                steps: pendingSteps,
            });
            pendingSteps = [];
        }
        this._updateChatState(sessionId, (state) => ({
            ...state,
            messages: chatMessages,
            _pendingDisplayActions: pendingActions,
            agentSteps: pendingSteps,
            toolCallsToConfirm: pendingToolConfirmations,
        }));
        this.lastMessageStreamIds.update((map) => {
            const newMap = new Map(map);
            newMap.set(sessionId, last_message_stream_id || '');
            return newMap;
        });
        return provider;
    }
    addUserMessage(sessionId, text, files) {
        this._updateChatState(sessionId, (state) => ({
            ...state,
            messages: [...state.messages, { role: 'user', text, files }],
        }));
    }
    enqueueMessage(sessionId, message) {
        this._updateChatState(sessionId, (state) => ({
            ...state,
            queuedMessages: [...state.queuedMessages, message],
        }));
    }
    dequeueAllMessages(sessionId) {
        let dequeued = [];
        this._updateChatState(sessionId, (state) => {
            if (!state.queuedMessages.length) {
                return state;
            }
            dequeued = state.queuedMessages;
            return { ...state, queuedMessages: [] };
        });
        return dequeued;
    }
    markStreaming(sessionId) {
        this._updateChatState(sessionId, (state) => ({
            ...state,
            _currentAssistantText: '',
            _currentThinkingText: '',
            _pendingDisplayActions: [],
            isStreaming: true,
            isLoading: true,
            currentLoadingText: DEFAULT_LOADING_TEXT,
            agentSteps: [],
            toolCallsToConfirm: [],
        }));
    }
    markReconnecting(sessionId) {
        this._updateChatState(sessionId, (state) => ({
            ...state,
            _currentAssistantText: '',
            _currentThinkingText: '',
            isStreaming: true,
            isLoading: true,
            currentLoadingText: 'Reconnecting...',
            toolCallsToConfirm: [],
        }));
    }
    markIdle(sessionId) {
        this._updateChatState(sessionId, (state) => ({
            ...state,
            _currentAssistantText: '',
            _currentThinkingText: '',
            isStreaming: false,
            isLoading: false,
            currentLoadingText: DEFAULT_LOADING_TEXT,
        }));
    }
    markDisconnected(sessionId) {
        this._updateChatState(sessionId, (state) => ({
            ...state,
            isStreaming: false,
            isLoading: false,
            currentLoadingText: DEFAULT_LOADING_TEXT,
            _currentAssistantText: '',
            _currentThinkingText: '',
            _pendingDisplayActions: [],
            agentSteps: [],
            toolCallsToConfirm: [],
            queuedMessages: [],
        }));
    }
    resetSession(sessionId) {
        this._removeChatState(sessionId);
    }
    handleEvent(sessionId, event) {
        switch (event.type) {
            case 'text_delta':
                this._handleTextDelta(sessionId, event.data);
                break;
            case 'thinking_delta':
                this._handleThinkingDelta(sessionId, event.data);
                break;
            case 'tool_call_start':
                this._handleToolCallStart(sessionId, event.data);
                break;
            case 'tool_call_pending_confirmation':
                this._handleToolCallPendingConfirmation(sessionId, event.data);
                break;
            case 'tool_call_stop':
                this._handleToolCallStop(sessionId, event.data);
                break;
            case 'done':
                this._handleDone(sessionId);
                break;
            case 'error':
                this._handleError(sessionId, event.data);
                break;
            case 'usage_warning':
                this._handleUsageWarning(sessionId, event.data);
                break;
            case 'requesting_confirmation':
                this._handleRequestingConfirmation(sessionId, event.data);
                break;
            case 'interrupted':
                this._handleInterrupted(sessionId);
                break;
            case 'system_event_run_start':
                this._handleSystemEventRunStart(sessionId);
                break;
            default:
                break;
        }
    }
    // A reply to system notifications may follow the user's turn on the same stream, without a done
    // in between. Close out that turn (attaching its display actions) and start a fresh one so the
    // reply lands in its own message.
    _handleSystemEventRunStart(sessionId) {
        this._handleDone(sessionId);
        this.markStreaming(sessionId);
    }
    removeToolCallConfirmation(sessionId, toolUseId) {
        this._updateChatState(sessionId, (state) => {
            const remaining = state.toolCallsToConfirm.filter((tc) => tc.tool_use_id !== toolUseId);
            return {
                ...state,
                toolCallsToConfirm: remaining,
                isStreaming: remaining.length === 0 ? true : state.isStreaming,
                isLoading: remaining.length === 0 ? true : state.isLoading,
            };
        });
    }
    _handleThinkingDelta(sessionId, text) {
        this._updateChatState(sessionId, (state) => {
            const thinkingText = state._currentThinkingText + text;
            const last = state.agentSteps[state.agentSteps.length - 1];
            let agentSteps;
            if (last && last.type === 'thinking') {
                agentSteps = [...state.agentSteps.slice(0, -1), { ...last, description: thinkingText }];
            }
            else {
                agentSteps = [...state.agentSteps, { type: 'thinking', title: 'Thought', description: thinkingText }];
            }
            return {
                ...state,
                _currentThinkingText: thinkingText,
                agentSteps,
                currentLoadingText: 'Thinking...',
                isLoading: true,
            };
        });
    }
    _handleTextDelta(sessionId, text) {
        this._updateChatState(sessionId, (state) => {
            const isNewMessage = state._currentAssistantText === '';
            const currentText = state._currentAssistantText + text;
            let { messages, agentSteps, isLoading } = state;
            if (isNewMessage) {
                const steps = agentSteps.length ? [...agentSteps] : undefined;
                agentSteps = [];
                isLoading = false;
                messages = [...state.messages, { role: 'assistant', text: currentText, files: [], steps }];
            }
            else {
                const last = state.messages[state.messages.length - 1];
                if (last && last.role === 'assistant') {
                    messages = [...state.messages.slice(0, -1), { ...last, text: currentText }];
                }
                else {
                    ({ messages } = state);
                }
            }
            return {
                ...state,
                _currentAssistantText: currentText,
                _currentThinkingText: '',
                messages,
                agentSteps,
                isLoading,
            };
        });
    }
    _handleToolCallStart(sessionId, data) {
        this._updateChatState(sessionId, (state) => {
            const displayMessage = data.display_message ?? data.name;
            const step = {
                type: 'tool_call',
                title: displayMessage,
                description: '',
                toolUseId: data.tool_use_id,
            };
            return {
                ...state,
                _currentThinkingText: '',
                _currentAssistantText: '',
                agentSteps: [...state.agentSteps, step],
                currentLoadingText: `${displayMessage}...`,
                isLoading: true,
            };
        });
    }
    _handleToolCallPendingConfirmation(sessionId, data) {
        // Loading-text-only update — no agent step. The tool hasn't run (and may never):
        // the workflow emits the real tool_call_start only after the user approves, and
        // requesting_confirmation clears this indicator when the prompt arrives.
        this._updateChatState(sessionId, (state) => ({
            ...state,
            currentLoadingText: `${data.display_message ?? data.name}...`,
            isLoading: true,
        }));
    }
    _handleToolCallStop(sessionId, data) {
        this.toolCallCompleted$.next({ name: data.name, displayResult: data.display_result });
        this._updateChatState(sessionId, (state) => {
            let { _pendingDisplayActions } = state;
            // Only chart results (state) and report downloads render below the message —
            // other tool links surface on their See Details steps. This also drops the
            // truthy-but-empty {} display_result that link-less tools return.
            const displayResult = data.display_result;
            if (displayResult && (displayResult.state || displayResult.type === 'download')) {
                _pendingDisplayActions = [
                    ..._pendingDisplayActions,
                    { name: data.name, display_result: displayResult },
                ];
            }
            let { agentSteps } = state;
            const displayMessage = data.display_message ?? data.name;
            const toolDescription = data.tool_description || displayMessage;
            // Report downloads render a dedicated download button below the message,
            // so their download url is not repeated on the step title.
            const stepUrl = displayResult?.type === 'download' ? undefined : displayResult?.url || undefined;
            const items = toStepItems(data.name, displayResult?.items);
            const hasExistingStep = agentSteps.some((step) => step.toolUseId === data.tool_use_id);
            if (hasExistingStep) {
                agentSteps = agentSteps.map((step) => step.toolUseId === data.tool_use_id
                    ? { ...step, title: toolDescription, url: stepUrl ?? step.url, items: items ?? step.items }
                    : step);
            }
            else {
                agentSteps = [
                    ...agentSteps,
                    {
                        type: 'tool_call',
                        title: toolDescription,
                        description: '',
                        toolUseId: data.tool_use_id,
                        url: stepUrl,
                        items,
                    },
                ];
            }
            return {
                ...state,
                _currentAssistantText: '',
                _currentThinkingText: '',
                _pendingDisplayActions,
                agentSteps,
            };
        });
    }
    _handleDone(sessionId) {
        this._updateChatState(sessionId, (state) => {
            let { messages } = state;
            const actions = state._pendingDisplayActions.length ? state._pendingDisplayActions : undefined;
            let actionsAttached = false;
            const currentSteps = state.agentSteps;
            if (currentSteps.length && !state._currentAssistantText) {
                messages = [
                    ...messages,
                    {
                        role: 'assistant',
                        text: '',
                        files: [],
                        steps: [...currentSteps],
                        displayActions: actions,
                    },
                ];
                actionsAttached = true;
            }
            if (state._currentAssistantText || actions?.length) {
                const last = messages[messages.length - 1];
                if (last && last.role === 'assistant') {
                    messages = [...messages.slice(0, -1), { ...last, displayActions: actions }];
                    actionsAttached = true;
                }
            }
            return {
                ...state,
                messages,
                _currentAssistantText: '',
                _currentThinkingText: '',
                // A done that attached nothing (e.g. the transport's synthetic done after a
                // silent reconnect) must not destroy actions a later real done can attach.
                _pendingDisplayActions: actionsAttached ? [] : state._pendingDisplayActions,
                isStreaming: false,
                isLoading: false,
                currentLoadingText: DEFAULT_LOADING_TEXT,
                agentSteps: [],
                toolCallsToConfirm: [],
            };
        });
    }
    _handleUsageWarning(sessionId, text) {
        // Unlike errors, the run is still streaming. Append the nudge and leave the loading and
        // streaming state alone so the agent's response renders after it as a separate message.
        this._updateChatState(sessionId, (state) => ({
            ...state,
            messages: [...state.messages, { role: 'assistant', text, files: [] }],
        }));
    }
    _handleError(sessionId, errorText) {
        this._updateChatState(sessionId, (state) => ({
            ...state,
            messages: [...state.messages, { role: 'assistant', text: errorText, files: [] }],
            _currentAssistantText: '',
            _currentThinkingText: '',
            isStreaming: false,
            isLoading: false,
            currentLoadingText: DEFAULT_LOADING_TEXT,
            agentSteps: [],
            toolCallsToConfirm: [],
        }));
    }
    _handleRequestingConfirmation(sessionId, data) {
        this._updateChatState(sessionId, (state) => {
            let { messages } = state;
            const actions = state._pendingDisplayActions.length ? state._pendingDisplayActions : undefined;
            if (state._currentAssistantText || actions?.length) {
                const last = messages[messages.length - 1];
                if (last && last.role === 'assistant') {
                    messages = [...messages.slice(0, -1), { ...last, displayActions: actions }];
                }
            }
            // Keep agentSteps in the live slot (do NOT flush them into a message or clear them):
            // if the user approves, the in-progress thinking step folds into the resulting response
            // message; if they deny, it folds into the next assistant text rather than orphaning.
            return {
                ...state,
                messages,
                _currentAssistantText: '',
                _currentThinkingText: '',
                _pendingDisplayActions: [],
                toolCallsToConfirm: data,
                isStreaming: false,
                isLoading: false,
                currentLoadingText: DEFAULT_LOADING_TEXT,
            };
        });
    }
    _handleInterrupted(sessionId) {
        this._updateChatState(sessionId, (state) => ({
            ...state,
            _currentAssistantText: '',
            _currentThinkingText: '',
            isStreaming: false,
            isLoading: false,
            currentLoadingText: DEFAULT_LOADING_TEXT,
            agentSteps: [],
            toolCallsToConfirm: [],
        }));
    }
    _updateChatState(sessionId, updater) {
        this.chatStates.update((map) => {
            const newMap = new Map(map);
            const current = newMap.get(sessionId) ?? createEmptyChatState();
            newMap.set(sessionId, updater(current));
            return newMap;
        });
    }
    _removeChatState(sessionId) {
        this.chatStates.update((map) => {
            const newMap = new Map(map);
            newMap.delete(sessionId);
            return newMap;
        });
    }
}
AgentSessionStateService.ɵfac = function AgentSessionStateService_Factory(__ngFactoryType__) { return new (__ngFactoryType__ || AgentSessionStateService)(); };
AgentSessionStateService.ɵprov = /*@__PURE__*/ i0.ɵɵdefineInjectable({ token: AgentSessionStateService, factory: AgentSessionStateService.ɵfac, providedIn: 'root' });
